import { XIcon } from "lucide-react";
import { useContext, useEffect, useLayoutEffect, useRef } from "react";

import { PopperSheetContext } from "@/components/atoms/Popper";
import { useListContext } from "./List.hooks";

import { Button } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { cn } from "@/components/ui/utils";

export function ListSearchInput({
  placeholder = "Search...",
}: {
  placeholder?: string;
}) {
  const {
    filter,
    setFilter,
    isLoading,
    size,
    classes,
    autofocus,
    setSearchBarHeight,
  } = useListContext();
  const searchBarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // In a popper, it makes the popper a sheet on a phone - before it is drawn,
  // so it never shows as a box first. There it is the sheet's bar: a field of
  // a phone's own size, and Cancel beside it, as a phone's search has.
  const sheet = useContext(PopperSheetContext);
  const setHasSearch = sheet?.setHasSearch;
  useLayoutEffect(() => {
    if (!setHasSearch) return;
    setHasSearch(true);
    return () => setHasSearch(false);
  }, [setHasSearch]);
  const isSheet = sheet?.isSheet ?? false;

  // Measure search bar height and update context
  useEffect(() => {
    if (searchBarRef.current) {
      const measureHeight = () => {
        const height = searchBarRef.current?.offsetHeight;
        if (height !== undefined && height > 0) {
          setSearchBarHeight(height);
        }
      };

      measureHeight();
      const timeoutId = setTimeout(measureHeight, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [setSearchBarHeight]);

  // Focus the input when autofocus is true (handles programmatic opening)
  // We also need to retry when isLoading changes from true to false,
  // because a disabled input cannot receive focus.
  useEffect(() => {
    if (autofocus && inputRef.current && !isLoading) {
      // Use requestAnimationFrame to ensure the Popper is fully rendered
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [autofocus, isLoading]);

  return (
    <div
      ref={searchBarRef}
      className={cn(
        "w-full bg-popover shrink-0",
        classes.listItem,
        "[border-bottom-width:1px] [border-bottom-style:solid] border-border",
        isSheet && "flex items-center gap-2 pl-3 pr-3 pt-2 pb-2",
      )}
    >
      <InputGroup
        className={cn(
          "border-0 bg-transparent px-2 shadow-none dark:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0",
          !isSheet && size === "sm" && "h-8",
          isSheet && "flex-1 min-w-0",
        )}
      >
        <InputGroupInput
          ref={inputRef}
          autoFocus={false}
          value={filter}
          disabled={isLoading}
          placeholder={placeholder}
          onChange={(e) => setFilter(e.target.value)}
          // A phone's search keyboard; its own clear button stays this one.
          inputMode="search"
          enterKeyHint="search"
          // The list's text, as the row that makes one names what is typed.
          className={cn("px-0", classes.text)}
        />
        <InputGroupAddon align="inline-end">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Clear"
            disabled={filter === ""}
            onClick={() => setFilter("")}
            className="[&_svg]:w-4 [&_svg]:h-4 [&_svg]:[stroke-width:0.4]"
          >
            <XIcon />
          </Button>
        </InputGroupAddon>
      </InputGroup>
      {isSheet ? (
        <Button
          variant="ghost"
          onClick={sheet?.close}
          data-testid="popper-sheet-cancel"
          className="shrink-0"
        >
          Cancel
        </Button>
      ) : null}
    </div>
  );
}

ListSearchInput.displayName = "List.SearchInput";
/** Stable marker so ``List`` can recognize search children across HMR. */
(
  ListSearchInput as typeof ListSearchInput & {
    __isListSearchInput?: boolean;
  }
).__isListSearchInput = true;
